import React, { useState, useEffect, useContext, useCallback } from "react";
import { Modal } from "antd";
import Tabs from "react-draggable-tabs";
import { VISITOR_CHAT_DRAWER_WIDTH, VISITOR_CHAT_DRAWER_WIDTH_COMPACT } from "../dragableModal/dragableModal";
import EndChatWithAiSessionModal from "../EndChatWithAiSessionModal";
import { isCurrentAgentServingVisitor, shouldConfirmEndEntireSession } from "../../helpers/agentSessionEnd";
import "./bottomTabs.scss";
import { BellFilled } from "@ant-design/icons";
import { SocketContext } from '../../context/socketContext';
import { useDispatch, useSelector } from "react-redux";
import { onLeaveServed, removeMessageVisitor } from "../../redux/VisitorsJoined/VisitorsJoinedActions";
import { dedupeActiveVisitors, normalizeVisitorUniqueId, visitorsUniqueKeyEqual } from "../../helpers/visitorId";

export default function BottomTabs({
  AllActiveVisitors,
  setAllActiveVisitors,
  collapsed,
  visitorDrawerCompact = true,
}) {
  const [tabs, setTabs] = useState([]);
  const { Socket } = useContext(SocketContext);
  const userData = useSelector((state) => state.Auth.userData);
  const servedData = useSelector((state) => state.VisitorReducer.data);
  const visitorMessageId = useSelector((state) => state.VisitorReducer.visitorMessageId);
  const dispatch = useDispatch();
  const [endSessionConfirmVisitor, setEndSessionConfirmVisitor] = useState(null);

  const completeTabPanelClose = useCallback(
    (id, { endEntireChat = false } = {}) => {
      const findVisitor = AllActiveVisitors?.find((x) => visitorsUniqueKeyEqual(x?.unique_id, id));
      if (
        findVisitor?.current_conversation?.agentParticipants?.some(
          (x) => String(x?._id) === String(userData?._id)
        )
      ) {
        if (Socket) {
          Socket.emit("agentLeaveRoom", {
            agentId: userData?._id,
            unique_id: findVisitor?.unique_id,
            endEntireChat: Boolean(endEntireChat),
          });
        }
        dispatch(onLeaveServed(findVisitor, servedData));
      }
      setAllActiveVisitors((visitors) => {
        const toggled = visitors?.map((visitor) =>
          visitorsUniqueKeyEqual(visitor.unique_id, id) ? { ...visitor, isOpen: !visitor.isOpen } : visitor
        );
        const wasInList = visitors?.some((v) => visitorsUniqueKeyEqual(v?.unique_id, id));
        if (wasInList) {
          return toggled?.filter((visitor) => !visitorsUniqueKeyEqual(visitor.unique_id, id));
        }
        return toggled;
      });
    },
    [AllActiveVisitors, dispatch, servedData, setAllActiveVisitors, Socket, userData?._id]
  );

  const Maximized = (id) => {
    const findID = visitorMessageId?.filter((x) => !visitorsUniqueKeyEqual(x?.unique_id, id));
    dispatch(removeMessageVisitor(findID));
    setAllActiveVisitors((visitors) =>
      visitors.map((visitor) =>
        visitorsUniqueKeyEqual(visitor.unique_id, id)
          ? { ...visitor, minimized: false, active: true, zIndex: 1 }
          : { ...visitor, minimized: true, zIndex: 0, active: false }
      )
    );
  };

  useEffect(() => {
    if (AllActiveVisitors?.length > 0) {
      const find = dedupeActiveVisitors(AllActiveVisitors)?.map((x) => {
        const findId = visitorMessageId?.find((i) => visitorsUniqueKeyEqual(i?.unique_id, x?.unique_id));
        return {
          id: x?.unique_id,
          active: true,
          content: (
            <div className={findId ? "tab_bottom_content blink" : "tab_bottom_content"}>
              {x?.name != null && x?.name !== "" ? (
                <p>
                  {x?.name}
                  {findId ? (
                    <span className="span_noti">
                      <span className="span_noti_relative">
                        {/* {findId && <BellFilled />} */}
                        <p className="header-general-pulse">{findId?.value}</p>
                      </span>
                    </span>
                  ) : (
                    ""
                  )}
                </p>
              ) : (
                <p>
                  {x?.unique_id}
                  {findId ? (
                    <span className="span_noti">
                      <span className="span_noti_relative">
                        {/* {findId && <BellFilled />} */}
                        <p className="header-general-pulse">{findId?.value}</p>
                      </span>
                    </span>
                  ) : (
                    ""
                  )}
                </p>
              )}
            </div>
          ),
          ...x,
        };
      });
      setTabs(find);
    } else {
      setTabs([]);
    }
  }, [AllActiveVisitors, visitorMessageId]);

  const moveTab = (dragIndex, hoverIndex) => {
    setTabs((prevTabs) => {
      const newTabs = [...prevTabs];
      const item = newTabs.splice(dragIndex, 1)[0];
      newTabs.splice(hoverIndex, 0, item);
      return newTabs;
    });
  };

  const selectTab = (selectedIndex, selectedID) => {
    setTabs((prevTabs) => {
      return prevTabs.map((tab) => ({
        ...tab,
        active: tab.id === selectedID,
      }));
    });
    Maximized(selectedID);
  };

  const finishCloseTab = useCallback((removedIndex, removedID) => {
    setTabs((prevTabs) => {
      const newTabs = prevTabs.map((t) => ({ ...t }));
      const removed = prevTabs[removedIndex];
      newTabs.splice(removedIndex, 1);

      if (removed?.active && newTabs.length !== 0) {
        const newActiveIndex = Math.min(removedIndex, newTabs.length - 1);
        newTabs.forEach((tab, i) => {
          tab.active = i === newActiveIndex;
        });
      }

      return newTabs;
    });
    completeTabPanelClose(removedID, { endEntireChat: false });
  }, [completeTabPanelClose]);

  const closedTab = (removedIndex, removedID) => {
    const findVisitor = AllActiveVisitors?.find((x) => visitorsUniqueKeyEqual(x?.unique_id, removedID));
    if (shouldConfirmEndEntireSession(findVisitor, userData?._id)) {
      setEndSessionConfirmVisitor(findVisitor);
      return;
    }
    if (isCurrentAgentServingVisitor(findVisitor, userData?._id)) {
      Modal.confirm({
        title: "End this chat?",
        content:
          "You are serving this visitor. Close the tab to end the chat and stop serving this conversation?",
        okText: "End chat",
        cancelText: "Stay",
        zIndex: 1300,
        centered: true,
        onOk: () => finishCloseTab(removedIndex, removedID),
      });
      return;
    }
    finishCloseTab(removedIndex, removedID);
  };

  // const activeTab = tabs.filter((tab) => tab.active === true);

  const visitorDrawerOpen =
    AllActiveVisitors?.length > 0 && AllActiveVisitors.some((v) => !v.minimized);

  const endChatVisitorName =
    endSessionConfirmVisitor?.name ||
    normalizeVisitorUniqueId(endSessionConfirmVisitor?.unique_id) ||
    null;

  return (
    <>
      <EndChatWithAiSessionModal
        open={Boolean(endSessionConfirmVisitor)}
        visitorName={endChatVisitorName}
        onCancel={() => setEndSessionConfirmVisitor(null)}
        onConfirm={() => {
          const ref = endSessionConfirmVisitor;
          setEndSessionConfirmVisitor(null);
          if (!ref) return;
          const current =
            AllActiveVisitors?.find((x) => visitorsUniqueKeyEqual(x?.unique_id, ref.unique_id)) ?? ref;
          const endEntire = shouldConfirmEndEntireSession(current, userData?._id);
          completeTabPanelClose(current.unique_id, { endEntireChat: endEntire });
        }}
      />
      <div
        style={{
          paddingLeft: collapsed ? "80px" : "200px",
          paddingRight: visitorDrawerOpen
            ? visitorDrawerCompact
              ? VISITOR_CHAT_DRAWER_WIDTH_COMPACT
              : VISITOR_CHAT_DRAWER_WIDTH
            : 0,
          boxSizing: "border-box",
        }}
        className="bottom_visitors_tab_main"
      >
        <Tabs
          moveTab={moveTab}
          selectTab={selectTab}
          closeTab={closedTab}
          tabs={tabs}
        ></Tabs>
      </div>
    </>
  );
}
